import { memo } from "react";

import WebshopLeadSection from "@/components/WebshopLeadSection";
import CroCalculator from "@/components/CroCalculator";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { Star } from "lucide-react";

import homeEcommerceFlow from "@/assets/homepage-ecommerce-flow.png.asset.json";
import homeEcommerceFlowMobile from "@/assets/homepage-ecommerce-flow-mobile.png.asset.json";
import USPSection from "@/components/USPSection";
import { openWhatsAppChat } from "@/components/LiveChat";
import TrustLogosSection from "@/components/TrustLogosSection";
import forevadifferentImg from "@/assets/portfolio/forevadifferent-square.webp";
import diamonddetailingImg from "@/assets/portfolio/diamonddetailing-square.webp";
import EmailReviewsSection from "@/components/EmailReviewsSection";
import { scrollToPlanner } from "@/lib/scrollToPlanner";

const Home = () => {
  return <div className="flex flex-col">
      <SEO title="Webshop vast op €20K–€50K per maand? | Nextconvert" description="Wij helpen webshops die vastzitten op €20K–€50K omzet per maand om winstgevender te schalen. Meer rendement uit je traffic met CRO, A/B-testen en e-mailmarketing." canonical="/" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Nextconvert",
            "url": "https://nextconvert.nl",
            "description": "E-commerce bureau dat webshops met €20K–€50K omzet per maand helpt winstgevender te schalen met conversieoptimalisatie, A/B-testen en e-mailmarketing met Klaviyo.",
            "email": "info@nextconvert.nl",
            "areaServed": "NL",
            "knowsAbout": ["Shopify webshop laten maken", "e-mailmarketing", "Klaviyo", "webshop optimalisatie", "webshop SEO", "conversie verhogen", "AI-oplossingen"],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "12"
            }
          })}
        </script>
      </Helmet>
      
      {/* AI-vriendelijke introductie sectie */}
      <div className="sr-only">
        <h2>Over <span className="accent-serif">Nextconvert</span></h2>
        <p>Nextconvert helpt webshops die vastzitten op €20K–€50K omzet per maand om winstgevender te schalen. We halen meer omzet uit bestaande bezoekers en klanten met CRO, A/B-testen en Klaviyo e-mailmarketing.</p>
      </div>
      
      {/* Hero Banner - Large centered with floating images */}
      <section className="bg-primary w-full relative overflow-hidden">
        {/* Floating decorative images - desktop only */}
        <div className="hidden lg:block">
          <img
            src={forevadifferentImg}
            alt=""
            className="absolute left-[5%] bottom-[15%] w-48 xl:w-56 rounded-2xl object-cover shadow-2xl rotate-[-4deg] opacity-90"
            loading="eager"
          />
          <img
            src={diamonddetailingImg}
            alt=""
            className="absolute right-[4%] top-[12%] w-44 xl:w-52 rounded-2xl object-cover shadow-2xl rotate-[3deg] opacity-90"
            loading="eager"
          />
        </div>

        <div className="max-w-[820px] mx-auto px-5 pt-14 pb-8 md:pt-28 md:pb-16 flex flex-col items-center text-center relative z-10">
          <h1 className="font-bold tracking-tight text-white leading-[1.1] mb-5 text-4xl md:text-5xl lg:text-[3.1rem]">
            Webshop vast op €20K–€50K per maand?
          </h1>
          <p className="text-white/70 text-sm md:text-base lg:text-lg leading-relaxed max-w-[580px] mb-8">
            Wij helpen je winstgevender groeien. Genoeg bezoekers is niet het probleem: in je webshop blijft nog veel omzet liggen. We analyseren en verbeteren de hele klantreis, van advertentie tot aankoop en herhaalaankoop via e-mail.
          </p>
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-12">
            <Button 
              onClick={() => document.getElementById('ontdek-omzet')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 md:px-8 h-11 md:h-12 rounded-full text-sm md:text-base"
            >
              Plan gratis gesprek
            </Button>
            <Button asChild variant="outline" className="border-white/30 text-white hover:bg-cream/10 hover:text-white font-medium px-7 md:px-8 h-11 md:h-12 rounded-full text-sm md:text-base bg-transparent">
              <button onClick={openWhatsAppChat}>Start een chat</button>
            </Button>
          </div>
          {/* Google Reviews indicator */}
          <div className="flex items-center gap-3 mb-0">
            <img alt="Google reviews logo" className="w-9 h-9 rounded-lg object-cover" src="/__l5e/assets-v1/58ce976d-cbb5-4e59-b01e-11eacfe84dc9/google-icon.jpg" />
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <span className="text-white/60 text-xs">Beoordeeld met een 5.0</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <TrustLogosSection />

      {/* Herken je dit? Section */}
      <section className="px-4 md:px-12 py-12 md:py-20 bg-cream">
        <div className="max-w-[1400px] mx-auto">
          <span className="text-accent font-semibold text-xs tracking-wide uppercase mb-4 block">Herken je dit?</span>
          <h2 className="text-3xl md:text-4xl font-semibold text-primary leading-[1.15] mb-8 md:mb-10">
            Genoeg bezoekers, maar de <span className="accent-serif italic">omzet</span> groeit niet mee
          </h2>
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                title: "Hoge advertentiekosten",
                description: "Je geeft elke maand meer uit aan advertenties, maar de winst blijft gelijk. Meer budget schaalt je alleen duurder."
              },
              {
                title: "Lage conversie",
                description: "Er komen genoeg bezoekers op je webshop, maar te weinig van hen kopen. Er blijft onnodig veel omzet liggen."
              },
              {
                title: "Klanten die maar één keer kopen",
                description: "Na de eerste bestelling hoor je je klanten nooit meer. Terwijl herhaalaankopen het goedkoopste groeikanaal zijn."
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 md:p-8">
                <h3 className="text-lg md:text-xl font-semibold text-primary mb-2.5">{item.title}</h3>
                <p className="text-muted-foreground text-sm md:text-[0.935rem] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Nextconvert Section */}
      <USPSection
      title="Meer omzet uit je huidige verkeer."
      description="Wij optimaliseren hoe je meer omzet kan halen uit jouw huidige ads budget en klantensysteem. Zo haal jij meer winst uit jouw webshop."
      primaryButton={{ text: "Plan kennismaking", onClick: scrollToPlanner }}
      image={homeEcommerceFlow.url}
      imageMobile={homeEcommerceFlowMobile.url}
      imageFit="contain"
      imageAlt="Shopify en Klaviyo worden door Nextconvert omgezet in meer omzet"
      usps={[
      {
        title: "Van bezoeker naar klant",
        description: "Elke pagina is gebouwd om te converteren. Van productpagina tot checkout, wij optimaliseren voor meer verkopen."
      },
        {
          title: "Alles onder één dak",
          description: "Shopify webshops en een goed email systeem. Wij zetten alles voor je klaar. Wij zijn dagelijks beschikbaar voor vragen."
        },
        {
          title: "Snel live en altijd bereikbaar",
          description: "Snel geleverd, geen maanden wachten. Jij wilt verkopen, wij zorgen dat je er klaar voor bent!"
        }]
      } />

      {/* Reviews Section */}
      <EmailReviewsSection variant="home" />

      <CroCalculator />

      {/* Waarom Nextconvert Section - WhyNextconvert style */}
      <section className="px-4 md:px-12 py-12 md:py-20 bg-cream">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-start">
            {/* Left column */}
            <div>
              <span className="text-accent font-semibold text-sm tracking-wide uppercase mb-3 block">Waarom Nextconvert?</span>
              <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-primary leading-tight mb-5">Waarom Nextconvert? <span className="accent-serif">Wij</span> halen meer uit je webshop</h2>
              <p className="text-muted-foreground text-[0.95rem] leading-relaxed mb-8">
                Onze missie is webshops die vastzitten op €20K–€50K per maand winstgevender te laten schalen. Geen standaard oplossing, maar een aanpak gericht op meer rendement uit de bezoekers en klanten die je al hebt.
              </p>
              <Button 
                onClick={() => document.getElementById('ontdek-omzet')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 h-12 rounded-full text-base"
              >
                Plan gratis gesprek
              </Button>
            </div>

            {/* Right column - 2x2 grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {[
                { title: "Van bezoeker naar klant", description: "Elke website en webshop bouwen we strategisch op basis van bewezen principes. Elke pagina stuurt bezoekers doelgericht richting actie." },
                { title: "Design dat omzet creëert", description: "Een professioneel en doordacht design dat je merk versterkt en direct vertrouwen opbouwt. Bezoekers blijven langer, klikken sneller en converteren beter." },
                { title: "Supersnel. Zonder frictie.", description: "Geoptimaliseerd voor snelheid en gebruiksgemak op elk apparaat. Snelle laadtijden en een soepele ervaring zorgen voor minder afhakers en meer resultaat." },
                { title: "Groei stopt niet na de livegang", description: "Na de oplevering blijven we analyseren, optimaliseren en verbeteren. Met ons maandelijks abonnement zorg je dat jouw online aanwezigheid altijd op topniveau presteert en blijft groeien." }
              ].map((item, i) => (
                <div
                  key={i}
                  className="group relative bg-muted/40 border border-border/60 rounded-2xl p-7 md:p-8 transition-all duration-500 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 hover:border-accent/30 overflow-hidden"
                >
                  <span className="absolute -top-3 -right-2 text-[7rem] font-black text-primary/[0.04] leading-none select-none pointer-events-none transition-colors duration-500 group-hover:text-accent/[0.08]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="w-10 h-1 rounded-full bg-accent/60 mb-5 transition-all duration-500 group-hover:w-16 group-hover:bg-accent" />
                  <h3 className="text-lg md:text-xl font-bold text-primary mb-2.5 relative z-10">{item.title}</h3>
                  <p className="text-muted-foreground text-sm md:text-[0.935rem] leading-relaxed relative z-10">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <div id="ontdek-omzet">
        <WebshopLeadSection
          title={<>Ontdek waar jij <span className="text-accent">extra omzet</span> laat liggen...</>}
          description="Voor webshops die vastzitten op €20K–€50K omzet per maand. In een gratis kennismaking doen we een audit van je volledige klantreis en laten we je zien waar omzet blijft liggen, zonder dat je meer advertentiekosten hoeft te maken."
        />
      </div>

    </div>;
};
export default memo(Home);
